export interface Me {
  email: string;
  is_manager: boolean;
}

export type ContributorDecision = "agree" | "agree_with_comment" | "dispute";
export type OwnerDecision = "preserve" | "amend" | "nullify";
export type Severity = "none" | "low" | "medium" | "high" | "critical";

export interface Contributor {
  id: number;
  contributor: string;
  contributor_decision: ContributorDecision | null;
  contributor_comment: string | null;
  contributor_decision_date: string | null;
  owner_decision: OwnerDecision | null;
  owner_amended_severity: Severity | null;
  owner_comment: string | null;
  owner_comment_date: string | null;
  final_validity: boolean;
  final_severity: Severity;
}

export interface QualityEvent {
  id: number;
  date: string;
  review_id: number | null;
  category: string;
  reporter: string | null;
  owner: string;
  status: "open" | "disputed" | "closed";
  policy_document: string | null;
  policy_section: string | null;
  severity: Severity;
  summary: string;
  description: string;
  edited_by: string | null;
  edited_at: string | null;
  contributors: Contributor[];
}

export interface Review {
  id: number;
  date: string;
  trigger_type: string;
  repo: string | null;
  issue_code: string | null;
  issue_title: string | null;
  pr_number: number | null;
  commit_id: string | null;
  author_is_human: boolean;
  author_email: string | null;
  author_system: string | null;
  author_instructions: string | null;
  approved_by: string | null;
  approved_date: string | null;
  full_text: string;
  highest_severity_event: Severity;
  final_highest_severity_event: Severity | null;
  edited_by: string | null;
  edited_at: string | null;
  created_at: string;
  events: QualityEvent[];
}

/**
 * Review scope (see `backend/quality_ledger/api/reviews.py::list_reviews`):
 *
 * - `all`: everything the caller can see (everything for managers, personal
 *   involvement for non-managers).
 * - `involved`: personal involvement only — author / event owner / event
 *   reporter / named contributor. The slice the Yours tab uses for everyone.
 * - `contributor`: strictly the contributor slice (a subset of `involved`).
 */
export type Scope = "all" | "contributor" | "involved";

/**
 * Event scope. `all` = unrestricted (manager firehose); `involved` = only
 * events where you're the owner, reporter, or a named contributor.
 */
export type EventScope = "all" | "involved";

/**
 * Canonical severity ordering, least → most severe. Single source of truth for
 * every severity sort/axis/label in the app; `SEV_RANK`, `compareSeverity`, and
 * `severityLabel` all derive from it, and `SeverityBadge.vue` shares the label.
 */
export const SEVERITY_ORDER: Severity[] = ["none", "low", "medium", "high", "critical"];

export const SEV_RANK: Record<Severity, number> = Object.fromEntries(
  SEVERITY_ORDER.map((s, i) => [s, i]),
) as Record<Severity, number>;

/** Sort comparator for severities, least → most severe. */
export function compareSeverity(a: Severity, b: Severity): number {
  return (SEV_RANK[a] ?? -1) - (SEV_RANK[b] ?? -1);
}

/**
 * User-facing label for a severity: `none` renders as "Pass" (no quality
 * events is the positive outcome), everything else verbatim. Shared with
 * `SeverityBadge.vue` so analytics headers and badges never diverge.
 */
export function severityLabel(s: Severity): string {
  return s === "none" ? "Pass" : s;
}

/**
 * Per-event severity rollup, mirroring the backend's review-level rollup but
 * scoped to a single event. Returns the post-amendment max severity *only*
 * when it differs from the event's original severity AND the event is closed.
 *
 * Open or disputed events keep showing the original severity with no
 * strikethrough — there is no final state yet to amend down from.
 */
export function eventFinalSeverity(e: QualityEvent): Severity | null {
  if (e.status !== "closed") return null;
  const valid = e.contributors.filter((c) => c.final_validity);
  if (!valid.length) return null;
  let max: Severity = "none";
  for (const c of valid) {
    if (SEV_RANK[c.final_severity] > SEV_RANK[max]) {
      max = c.final_severity;
    }
  }
  return max !== e.severity ? max : null;
}

const TRIGGER_LABELS: Record<string, string> = {
  pr: "PR ready",
  issue_assigned: "Issue assigned",
  issue_designed: "Design complete",
  issue_done: "Issue done",
};

/**
 * Human-readable label for a review's `trigger_type`. Falls back to the
 * raw value for trigger types the frontend doesn't know about yet, so a
 * new backend trigger still renders something rather than nothing.
 */
export function triggerLabel(t: string): string {
  return TRIGGER_LABELS[t] ?? t;
}

/**
 * Error carrying the HTTP status of a failed request, so callers can branch on
 * it (403 "not yours" vs 404 "gone") without scraping the message string.
 */
export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function jsonOrThrow<T>(r: Response, label: string): Promise<T> {
  if (!r.ok) {
    const body = await r.text().catch(() => "");
    throw new ApiError(r.status, `${label} ${r.status}: ${body}`);
  }
  return r.json();
}

export async function fetchMe(): Promise<Me | null> {
  const r = await fetch("/auth/me", { credentials: "include" });
  if (r.status === 401) return null;
  return jsonOrThrow<Me>(r, "auth/me");
}

export async function fetchReviews(scope: Scope = "all"): Promise<Review[]> {
  const r = await fetch(`/api/reviews?scope=${scope}`, { credentials: "include" });
  return jsonOrThrow<Review[]>(r, "api/reviews");
}

export async function fetchReview(id: number): Promise<Review> {
  const r = await fetch(`/api/reviews/${id}`, { credentials: "include" });
  return jsonOrThrow<Review>(r, `api/reviews/${id}`);
}

/**
 * One column of the manager pending-response board: a single dev and the
 * approved reviews awaiting their quality-event response. A *padded* column
 * (a dev added purely to keep the board at least 4 wide) has
 * `pending_count === 0` and an empty `reviews` array.
 */
export interface PendingBoardColumn {
  dev: string;
  pending_count: number;
  reviews: Review[];
}

/**
 * Manager-only Kanban of approved reviews with pending QE responses. The
 * backend decides column order and padding (see
 * `design/actions.md` § Pending-response board); the frontend renders the
 * columns left-to-right exactly as received and does no grouping of its own.
 *
 * `minPendingDays` asks the backend to only count a review as pending once it
 * has been approved (released to the contributor) for at least that many days.
 * The filter is applied server-side because it changes which devs are pending,
 * and therefore the padding.
 */
export async function fetchPendingResponseBoard(
  minPendingDays = 0,
): Promise<PendingBoardColumn[]> {
  const r = await fetch(
    `/api/reviews/pending-response-board?min_pending_days=${minPendingDays}`,
    { credentials: "include" },
  );
  const data = await jsonOrThrow<{ columns: PendingBoardColumn[] }>(
    r,
    "pending-response-board",
  );
  return data.columns;
}

/**
 * Returns standalone quality events (not attached to any review) within the
 * requested scope. The Dashboard uses this because there review-attached
 * events are surfaced via their review's payload. The manager-only Analytics
 * view, which needs every event in one flat list, uses `fetchAllEvents` below.
 */
export async function fetchStandaloneEvents(
  scope: EventScope = "all",
): Promise<QualityEvent[]> {
  const r = await fetch(
    `/api/events?standalone=true&scope=${scope}`,
    { credentials: "include" },
  );
  return jsonOrThrow<QualityEvent[]>(r, "api/events");
}

/**
 * Manager firehose: every quality event the caller can see — both standalone
 * and review-attached — within the backend's archival window. This is the
 * deliberate counterpart to `fetchStandaloneEvents` (which is standalone-only):
 * the manager-only Analytics view needs ALL events in one flat list to pivot
 * over. The backend returns the manager firehose only to managers (and the
 * personal slice to everyone else), so this is safe to call unconditionally.
 */
export async function fetchAllEvents(): Promise<QualityEvent[]> {
  const r = await fetch("/api/events?scope=all", { credentials: "include" });
  return jsonOrThrow<QualityEvent[]>(r, "api/events?scope=all");
}

export interface CreateEventPayload {
  date: string;
  category: "process_deviation" | "technical_quality_issue";
  owner: string;
  severity: Exclude<Severity, "none">;
  summary: string;
  description: string;
  contributors: { contributor: string }[];
  // Reporter is set server-side from the session; do NOT include it. The
  // backend rejects POST bodies that carry a `reporter` field.
  review_id?: number | null;
  policy_document?: string | null;
  policy_section?: string | null;
}

export async function createEvent(payload: CreateEventPayload): Promise<QualityEvent> {
  const r = await fetch("/api/events", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow<QualityEvent>(r, "createEvent");
}

export async function approveReview(id: number): Promise<Review> {
  const r = await fetch(`/api/reviews/${id}/approve`, {
    method: "POST",
    credentials: "include",
  });
  return jsonOrThrow<Review>(r, "approve");
}

export async function respondAsContributor(
  eventId: number,
  contributorId: number,
  decision: ContributorDecision,
  comment: string | null,
): Promise<QualityEvent> {
  const r = await fetch(`/api/events/${eventId}/contributors/${contributorId}/respond`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ decision, comment }),
  });
  return jsonOrThrow<QualityEvent>(r, "respond");
}

export async function finalizeAsOwner(
  eventId: number,
  contributorId: number,
  decision: OwnerDecision,
  amendedSeverity: Severity | null,
  comment: string | null,
): Promise<QualityEvent> {
  const r = await fetch(`/api/events/${eventId}/contributors/${contributorId}/finalize`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      decision,
      amended_severity: amendedSeverity,
      comment,
    }),
  });
  return jsonOrThrow<QualityEvent>(r, "finalize");
}

// --- Manager pre-approval curation -----------------------------------------
// See backend/quality_ledger/api/reviews.py + api/events.py. All four
// endpoints accept the request only while the parent review is unapproved
// (otherwise 409). Callers must already be in the manager role (otherwise
// 403). The UI gates the edit affordances on isManager && !approved_by, but
// the backend remains authoritative.

export interface ReviewPatchPayload {
  full_text?: string;
}

export interface EventPatchPayload {
  category?: "process_deviation" | "technical_quality_issue";
  severity?: Exclude<Severity, "none">;
  owner?: string;
  summary?: string;
  description?: string;
  policy_document?: string | null;
  policy_section?: string | null;
  contributors?: { contributor: string }[];
}

export async function updateReview(
  id: number,
  payload: ReviewPatchPayload,
): Promise<Review> {
  const r = await fetch(`/api/reviews/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow<Review>(r, "updateReview");
}

export async function addEventToReview(
  reviewId: number,
  payload: CreateEventPayload,
): Promise<QualityEvent> {
  // Backend forbids `review_id` in this payload (the path locates the
  // review) and `reporter` (set server-side from the session manager).
  const { review_id: _ignored1, ...body } = payload;
  void _ignored1;
  const r = await fetch(`/api/reviews/${reviewId}/events`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow<QualityEvent>(r, "addEventToReview");
}

export async function updateEvent(
  id: number,
  payload: EventPatchPayload,
): Promise<QualityEvent> {
  const r = await fetch(`/api/events/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow<QualityEvent>(r, "updateEvent");
}

export async function deleteEvent(id: number): Promise<void> {
  const r = await fetch(`/api/events/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) {
    const body = await r.text().catch(() => "");
    throw new Error(`deleteEvent ${r.status}: ${body}`);
  }
}

export async function deleteReview(id: number): Promise<void> {
  // Manager-only, unapproved-only (otherwise 409). Cascades to the review's
  // quality events and their contributor rows server-side.
  const r = await fetch(`/api/reviews/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) {
    const body = await r.text().catch(() => "");
    throw new Error(`deleteReview ${r.status}: ${body}`);
  }
}

export interface BulkDeleteResult {
  deleted_reviews: number;
  deleted_events: number;
}

export async function bulkDeleteReviews(ids: number[]): Promise<BulkDeleteResult> {
  // All-or-nothing: the backend rejects the whole batch (409/404) if any id is
  // approved or missing, deleting nothing. Returns the counts removed.
  const r = await fetch(`/api/reviews/bulk-delete`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  return jsonOrThrow<BulkDeleteResult>(r, "bulkDeleteReviews");
}
